import React from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CategoryIcon } from '@/components/ui/category-icon';
import { Category } from '@/types';
import { cn } from '@/lib/utils';

interface TransactionCategoryFieldEnhancedProps {
  value: string;
  onChange: (value: string) => void;
  categories: Category[];
  error?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

export const TransactionCategoryFieldEnhanced: React.FC<TransactionCategoryFieldEnhancedProps> = ({ 
  value, 
  onChange, 
  categories, 
  error, 
  disabled = false,
  className,
  label = "Categoria",
  placeholder = "Selecionar categoria",
  required = false
}) => {
  const selectedCategory = categories.find(cat => cat.id === value);

  return (
    <div className={cn("space-y-2", className)}>
      <Label 
        htmlFor="categoryId" 
        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
      >
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      
      <Select 
        value={value} 
        onValueChange={onChange} 
        disabled={disabled}
        required={required}
      >
        <SelectTrigger 
          className={cn(
            "touch-target rounded-xl bg-background",
            error && "border-destructive focus:ring-destructive",
            !selectedCategory && "text-muted-foreground"
          )}
          id="categoryId"
        >
          <SelectValue placeholder={placeholder}>
            {selectedCategory && (
              <div className="flex items-center gap-2">
                <CategoryIcon
                  iconName={selectedCategory.icon}
                  categoryName={selectedCategory.name}
                  color={selectedCategory.color}
                  size="sm"
                />
                <span className="truncate">{selectedCategory.name}</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        
        <SelectContent>
          {categories.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Nenhuma categoria disponível
            </div>
          ) : (
            categories.map(category => (
              <SelectItem 
                key={category.id} 
                value={category.id}
                className="cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <CategoryIcon
                    iconName={category.icon}
                    categoryName={category.name}
                    color={category.color}
                    size="sm"
                  />
                  <span className="truncate">{category.name}</span>
                </div>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
};